import { Button, SpaceBetween } from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import { useApi } from 'src/hooks/useApi';
import { useTranslate } from 'src/i18n/useTranslate';
import './TopicRating.scss';

interface TopicRatingProps {
    artifactId: string;
    artifactTitle: string;
    topicId: string;
    topicTitle: string;
}

export default function TopicRating({
    artifactId,
    artifactTitle,
    topicId,
    topicTitle,
}: TopicRatingProps) {
    const t = useTranslate();
    const [expanded, setExpanded] = useState(false);
    const [hoveredStar, setHoveredStar] = useState(0);
    const [submitted, setSubmitted] = useState(false);

    const { makeRequest } = useApi({ method: 'POST', url: '/rating' });

    useEffect(() => {
        setExpanded(false);
        setHoveredStar(0);
        setSubmitted(false);
    }, [topicId]);

    const handleRate = (value: number) => {
        setSubmitted(true);
        setExpanded(false);
        makeRequest({
            data: {
                rating: value,
                artifactId,
                topicId,
                pageUrl: window.location.href,
            },
        });
    };

    if (!expanded) {
        return (
            <div
                data-topic-rating
                style={{
                    transformOrigin: 'center center',
                    display: 'inline-block',
                }}
            >
                <Button
                    variant="normal"
                    iconName={submitted ? 'status-positive' : 'star'}
                    ariaLabel={
                        submitted
                            ? t('thank_you', 'Thank you!')
                            : t('rate_topic', 'Rate this topic')
                    }
                    onClick={() => {
                        if (!submitted) setExpanded(true);
                    }}
                    data-testid="topic-rating-toggle"
                    data-topic-rating-button
                >
                    {submitted
                        ? t('thank_you', 'Thank you!')
                        : t('rate_topic', 'Rate this topic')}
                </Button>
            </div>
        );
    }

    return (
        <div
            data-topic-rating
            data-topic-rating-expanded
            style={{
                transformOrigin: 'center center',
                display: 'inline-block',
            }}
        >
            <SpaceBetween size="xxxs" direction="horizontal">
                {[1, 2, 3, 4, 5].map((n) => (
                    <div
                        key={n}
                        onMouseEnter={() => setHoveredStar(n)}
                        onMouseLeave={() => setHoveredStar(0)}
                    >
                        <Button
                            data-testid={`topic-rating-${n}-star`}
                            data-topic-rating-star={
                                hoveredStar >= n ? 'hovered' : 'empty'
                            }
                            iconName={hoveredStar >= n ? 'star-filled' : 'star'}
                            variant="icon"
                            iconAlt={`${n} star`}
                            onClick={() => handleRate(n)}
                        />
                    </div>
                ))}
            </SpaceBetween>
        </div>
    );
}
